import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from '../../utils/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ChartBarIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

const AyudanteDashboard: React.FC = () => {
  const { user } = useAuth();
  
  // Fetch cantidad de notas cargadas por el usuario
  const { data: cantidadNotasCargadas, isLoading: loadingNotas } = useQuery({
    queryKey: ['notas-cargadas-usuario', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      const response = await axios.get(`/notas/usuario/${user.id}/cantidad-cargadas`);
      return response.data;
    },
    enabled: !!user?.id,
  });

  const quickActions = [
    { 
      name: 'Cargar Notas', 
      href: '/admin/notas', 
      icon: ChartBarIcon, 
      color: 'green',
      description: 'Ingresar calificaciones de alumnos'
    },
  ];

  const recentActivity = [
    { id: 1, message: 'Nueva inscripción en tu turno', time: '2 min ago', type: 'info' },
    { id: 2, message: 'Nota cargada exitosamente', time: '1 hora ago', type: 'success' },
    { id: 3, message: 'Alumno consultó sobre horarios', time: '2 horas ago', type: 'info' },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">¡Bienvenido, Ayudante!</h1>
            <p className="text-green-100 mt-1">Panel de gestión de notas</p>
          </div>
          <div className="hidden md:block">
            <AcademicCapIcon className="h-16 w-16 text-green-200" />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Notas Cargadas</p>
              <p className="text-2xl font-bold text-gray-900">
                {loadingNotas ? '...' : (cantidadNotasCargadas || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones Principales</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quickActions.map((action) => (
            <Link
              key={action.name}
              to={action.href}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:shadow-md hover:border-gray-300 transition-all duration-200"
            >
              <div className={`p-2 bg-${action.color}-100 rounded-lg`}>
                <action.icon className={`h-5 w-5 text-${action.color}-600`} />
              </div>
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900">{action.name}</span>
                <p className="text-xs text-gray-500">{action.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actividad Reciente</h3>
        <div className="space-y-4">
          {recentActivity.map((activity) => (
            <div key={activity.id} className="flex items-center space-x-3">
              <div className={`w-2 h-2 rounded-full ${
                activity.type === 'success' ? 'bg-green-500' : 
                activity.type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
              }`}></div>
              <p className="text-sm text-gray-600">{activity.message}</p>
              <span className="text-xs text-gray-400">{activity.time}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">¿Necesitas ayuda?</h3>
        <p className="text-sm text-blue-700 mb-4">
          Como ayudante, puedes cargar notas de los alumnos.
        </p>
        <div className="text-sm text-blue-600">
          <p>• <strong>Notas:</strong> Carga las calificaciones de los exámenes</p>
          <p>• <strong>Consulta:</strong> Contacta al JTP si necesitas ayuda adicional</p>
        </div>
      </div>
    </div>
  );
};

export default AyudanteDashboard;
